import { EndRepeatEnum, TimeFrameEnum } from '@app/react/types/Booking';
import { ICustomer } from '@app/react/types/customers';
import { CurrencyEnum, IInvoice, PlatformsEnum, ProductTypesEnum } from '@app/react/types/orders';
import { EPaymentMethod, EPaymentStatus, IPayingUser } from '@app/react/types/payment';
import { Product } from '@app/react/types/product';
import { EReservationStatus, MaintenanceDto, UpdateReservationDto } from '@app/react/types/reservation';
import { Slot } from '@app/react/types/slot';
import {
	AddonDto,
	InstructorResourceDto,
	PaymentMethodTypeEnum,
	ProblematicSlotDto,
	ProductDto,
	ReservationTypeEnum,
	UserPunchPassDto,
	SimpleCustomerDto,
	SimpleFacilityDto,
	SpaceResourceDto,
	SportsEnum,
	UpdatePricesTypeEnum,
} from '@bondsports/types';

import { Icons, IOption, ISlot, TRadioType } from '@bondsports/utils';
import { VISIBILITY } from '../../shared/formBlocks/VisibilityBlock/constants';
import { SEGMENTS_ADDONS } from './flows/rental/step4/components/SegmentsAddons/constants';
import { RESERVATION_ADDONS } from './flows/rental/step4/components/ReservationAddons/constants';
import { CUSTOMER } from '@app/react/components/shared/formBlocks/SingleCustomerFormSelect/constants';
import { SimpleProductOption } from '../../shared/formBlocks/ReservationAddonsFormSelect/types';
import { HttpStatusEnum } from '@app/react/lib/network';
import { PricingFormValues } from '../../shared/reservations/pricing/types';
import { PRICING } from './flows/rental/consts';

export enum SlotTypeEnum {
	EXTERNAL = 'external',
	INTERNAL = 'internal',
	MAINTENANCE = 'maintenance',
	CUSTOM = 'custom',
}

export enum SegmentTypeEnum {
	RENTAL = 'rental',
	PASS = 'pass',
}

export enum PassModeEnum {
	REDEEM = 'redeem',
	PURCHASE = 'purchase',
}

export enum SlotDurationTypeEnum {
	DATES = 'dates',
	ALL_DAY = 'all day',
	DURATION = 'duration',
}

enum DurationUnitTypesEnum {
	MINUTES = 'minutes',
	HOURS = 'hours',
}

export enum FrequencyEnum {
	NONE = 'none',
	WEEKLY = 'weekly',
	DAILY = 'daily',
	MONTHLY = 'monthly',
	YEARLY = 'yearly',
}

enum MaintenanceTimingEnum {
	BEFORE = 1,
	AFTER = 2,
	AT_THE_BEGINING = 3,
	AT_THE_END = 4,
}

export enum InvoiceStatusEnum {
	ACTIVE = 'active',
	WAITING_ADMIN = 'waitingAdmin',
	WAITING_CLIENT = 'waitingClient',
	CANCELED = 'canceled',
}

export enum LineItemsStatusEnum {
	USER_PRODUCT = 'UserProduct',
	RENTAL_PRODUCT = 'RentalProduct',
}

enum CreatorTypeEnum {
	SPACE = 'space',
	PROGRAM_SEASON = 'program_season',
}

export type MaintenanceSlotDto = SlotDto & MaintenanceDto;

export interface SlotDto {
	// init after step 1
	title: string;
	creatorId: number;
	creatorType: string;
	userCreatorId: number;
	startDate: string;
	startTime?: string;
	endDate?: string;
	endTime?: string;
	spaceId: number;
	publicNotes?: string;
	privateNotes?: string;
	slotType: SlotTypeEnum;
	timezone?: string;
	sportIds: number[];
	parentSlotId?: number;
	maintenanceDurationdurationType?: DurationUnitTypesEnum;
	maintenanceTiming?: MaintenanceTimingEnum;
	durationValue?: number;
	paymentStatus: EPaymentStatus;
	approvalStatus: EReservationStatus;
	displayName?: string;
	internalName?: string;
	maintenance?: MaintenanceDto[];
	maintenanceSlots?: MaintenanceSlotDto[];

	relevantProducts?: Product[];
	product: Product;
	productMetadata?: { productId: number; price: number };
	totalPrice: number; // how to calculate

	// init by server in step 2
	addonProducts?: Product[];

	// init after step 4
	id?: number;
	reservationId?: number;
	segmentId?: number;
	seriesId?: number;
	eventId?: number;
	invoiceId?: number;
	addonsProductUserIds?: number[] | null;
	reservation?: any;
	event?: any;
	isPrivate: boolean;

	addons?: AddonDto[];
	addonsIds?: number[];
	conflicts?: Slot[];
	colorCodeId?: string | number;
}

export interface Duration {
	duration: boolean;
	allDay: boolean;
	durationEndsAfter?: number;
	durationUnit?: DurationUnitTypesEnum;
}

export interface SeriesDto {
	startDate: string;
	startTime?: string;
	endDate?: string;
	endTime?: string;
	slotDurationType: SlotDurationTypeEnum;
	durationEndsAfter?: number;
	durationUnit?: DurationUnitTypesEnum;
	frequency: FrequencyEnum;
	isRepeat: boolean;
	repeatEvery?: number;
	repeatOn?: number[];
	repeatEndDate?: string;
	numberOccurrences?: number;
	maintenance?: MaintenanceDto[];
	slots?: SlotDto[];
	id?: number;
	resources?: { id: number; name: string }[];
}

export type SeriesClientDto = SeriesDto & {
	duration: boolean;
	allDay: boolean;
	occurrencesType?: EndRepeatEnum;
	numberOfOccurrences?: number;
};

export interface SegmentDto {
	title: string;
	isPrivate: boolean;
	resourceIds: number[];
	sportIds: number[];
	series: SeriesDto[];

	publicNotesForSlots?: string;
	privateNotesForSlots?: string;

	addonIds?: number[];
	id: number;
	type: string; // birthday / practice / etc
	// tasks?:ITask[] // V3
	// isResourcesBlocked:boolean, // V3
	product?: Product;
	colorCodeId?: number | string;
}

export type SegmentsClientDto = Omit<SegmentDto, 'series'> & {
	sportId: string;
	series: SeriesClientDto[];
	visibility: VisibilityEnum;
};

export interface ReservationDto {
	customerId: number;
	name: string; //aka title in the client
	reservationType: ReservationTypeEnum;
	segments: SegmentDto[];
	description?: string;
	creatorId?: number;
	userCreatorId: number;
	privacySetting?: string;
	organizationId?: number;
	paymentStatus?: EPaymentStatus;
	creatorType?: CreatorTypeEnum;
	publicNotes?: string;
	privateNotes?: string;
	forms?: number[];
	addonProducts?: Product[];
	id?: number; // needed for update
	status?: EReservationStatus;
	invoiceId?: string;
	price?: number;
	addons?: any[];
	colorCodeId?: string | number;
	purchasingUserId?: number;
}

export type ReservationClientDto = Omit<ReservationDto, 'segments'> & {
	addons?: { productId: number; quantity: number }[];
	slotsBySeriesBySegments?: SlotDto[][][];
	segments: SegmentsClientDto[];
	optionalAddons?: Product[];
	optionalAddonsBySegments?: { [key: number]: Product[] };
	customer?: ICustomer;
	finalReservation: ReservationDto;
	resourcesProducts: any;
	invoice: any;
	colorCodeId?: string | number;
	reservationId?: string;
};

export interface LineItemsDto {
	resources?: ResourceDto[];
	type: LineItemsStatusEnum;
	organizationId: number;
	userId: number;
	product?: Product;
	productId: number;
	productType: ProductTypesEnum;
	ordinal: number;
	price: number;
	parentOrdinal?: number;
	originalPrice: number;
	paidAmount: number;
	currency: CurrencyEnum;
	paymentStatus: EPaymentStatus;
	isRefunded: boolean;
	isTaxInclusive: boolean;
	taxPrecent: number;
	unitPrice: number;
	quantity: number;
}

export interface InvoiceDto {
	invoiceId?: string | null;
	organizationId: number;
	price: number | null;
	status: InvoiceStatusEnum;
	lineItems: LineItemsDto[];
	paymentStatus: EPaymentStatus;
	currency: CurrencyEnum;
	creatingUserId: number;
	platform: PlatformsEnum;
}

interface ResourceDto {
	endDate: string;
	endTime: string;
	resourceId: number;
	resourceType: string;
	startDate: string;
	startTime: string;
}

export class PurchasePaymentDto {
	// token - id of payment method
	token: string;
	type: EPaymentMethod;
}
export class BookingDto {
	reservation: ReservationDto;
	platform: PlatformsEnum;
	shiftId?: number;
	paymentData: PurchasePaymentDto;
	amountToPay: number;
	total: number;
	purchasingUserId: number;
	invoiceId?: number;
}

export enum MassEditTypeEnum {
	INDIVIDUAL = 'individual',
	COLLECTIVELY = 'collectively',
}
// TimSlotData - the time slot data for calculatiing the end repeat section
export interface TimeSlotData {
	startDate: string;
	startTime: string;
	endTime: string;
	selectedDays?: number[];
	occurrences: number;
	frequencies?: FrequencyEnum;
	repeatEvery?: string;
	endRepeatDate?: string;
}

export interface ISlotId {
	slotIndex: number;
	segmentIndex: number;
	seriesIndex: number;
	sortedSlotIndex?: number;
}

export type ISlotDtoExpanded = SlotDto & ISlotId;
export type ClientSlotDto = ISlotDtoExpanded & { addonIds?: number[]; isAddons?: boolean; optionalAddons?: Product[] };

export interface SlotFormValues {
	sportId: number;
	title?: string;
	colorCodeId?: string | number;
	startDate: string;
	endDate: string;
	startTime: string;
	endTime: string;
	isPrivate: boolean;
	resourceIds?: number;
	isMaintenance?: boolean;
	maintenance?: MaintenanceDto[];
	publicNotes?: string;
	privateNotes?: string;
	seriesIndex?: number;
	slotIndex?: number;
	sortedSlotIndex?: number;
	segmentIndex?: number;
	isAddons?: boolean;
	addonIds?: (string | number)[];
	optionalAddons: IOption[];
	isDisplayChangeProductModal: boolean;
	isNewProduct: boolean;
	pricing_type: UpdatePricesTypeEnum;
	slotType: SlotTypeEnum;
}
export type MassEditFormValues = SlotFormValues & Duration;

export interface IndividualFormValues {
	individualForms?: MassEditFormValues[];
}
export type ISlotExpanded = ISlot & {
	slotIndex: number;
	eventIndex: number;
	segmentIndex: number;
	seriesIndex: number;
	sortedSlotIndex: number;
};

export interface AddSlotToSegmentDto {
	slot: SlotDto;
	addonIds?: number[];
}
export interface BaseInvoiceDto {
	platform: PlatformsEnum;
	shiftId?: number;
}
export interface SaveSlotToSegmentDto extends UpdateReservationDto {
	slots: SlotDto[];
}

export enum SlotActionsEnum {
	DUPLICATE = 'duplicate',
	DELETE = 'delete',
}

export interface IDeleteEventResponse {
	lineItems: {
		id: number;
		slotIds: number[];
		amount: number;
		quantity: number;
		canVoid: boolean;
		canRefund: boolean;
	}[];
	paymentMethods: {
		paymentType: PaymentMethodTypeEnum;
		paymentMethodId: string;
		fundLeft: number;
		ccLast4?: string;
		ccBrand?: string;
		payingUser?: IPayingUser;
	}[];
	nextFinancialStep: EFinancialSteps;
	invoice?: IInvoice;
}

export enum EFinancialSteps {
	VOID = 'void',
	REFUND = 'refund',
	NONE = 'none',
	REFUND_AND_VOID = 'refund-and-void',
	APPEND = 'append',
}

export interface IFormContextValues {
	//TODO: think of a better name?
	addons?: { productId: number; quantity: number }[];
	customer?: ICustomer;
	customerId: number;
	finalReservation: ReservationDto;
	isAddons?: boolean;
	name: string; //aka title in the client
	optionalAddons?: Product[];
	optionalAddonsBySegments?: { [key: number]: Product[] };
	order: any;
	pricing_type: UpdatePricesTypeEnum;
	reservationAddons: any;
	reservationType: ReservationTypeEnum;
	resourcesProducts: any;
	segments: SegmentsClientDto[];
	slotAddons: any;
	slotsBySeriesBySegments?: SlotDto[][][];
	userId: number;
}

export interface ICalculateAddonsResponse {
	reservation: ReservationDto;
	invoice: InvoiceDto;
}
//To do export from utils
interface IAddonProp {
	icon: Icons;
	title: string;
	price?: number;
	subTitle?: string;
	hasConflicts?: boolean;
	OnTriggerConflicts?: () => void;
}

export type ISummarySlot = {
	footerWithMaintenance?: string;
	footerWithoutMaintenance?: string;
	isMaintenancConflicting?: boolean;
	originalSlot: SlotDto;
	id?: string;
	index?: number;
	title: string;
	startDate?: string;
	startTime?: string;
	endDate?: string;
	endTime?: string;
	addons?: IAddonProp[];
	hasConflicts?: boolean;
	conflicts?: Slot[];
	spaceId: number;
} & ISlotId;

//use addons from SlotDto
//A temporary solution- need to refactor the how the summary slots are generated
export type IConflictsSummarySlot = Omit<ISummarySlot, 'addons'> & Omit<SlotDto, 'id'> & { id: string };

export type DraftEditData = ReservationDto & ISlotId & { conflicts: Slot[] };

export interface IRadio {
	componentType: TRadioType;
	description: string;
	title: string;
	value: MassEditTypeEnum | UpdatePricesTypeEnum;
}

export interface INewReservationMaintenanceFormValues {
	title: string;
	durationValue: number;
	maintenanceDurationdurationType: TimeFrameEnum;
	maintenanceTiming: MaintenanceTimingEnum;
}

export interface INewReservationSegmentsFormValues {
	resourceIds?: string[];
	sportId?: string;
	spaces?: SpaceResourceDto[];
	series: SeriesClientDto[];
	facility?: SimpleFacilityDto;
	isPrivate?: boolean;
	visibility?: VisibilityEnum;
}

export interface AddonAndQuantity {
	addon?: SimpleProductOption;
	quantity?: number;
	id?: string;
}

export interface SegmentAddon extends AddonAndQuantity {
	segmentIds?: string[];
	groupId?: string;
	id?: string;
}

export interface DraftReservationAddons {
	[RESERVATION_ADDONS]?: AddonAndQuantity[];
	[SEGMENTS_ADDONS]?: SegmentAddon[];
	removedSegmentsIds?: string[];
}

export interface ExtrasStep {
	extras?: DraftReservationAddons;
}

export interface PricingStep {
	[PRICING]?: PricingFormValues; //for external should be mandetory
}

export interface DraftReservationAdditionalSteps extends ExtrasStep, PricingStep {}

export interface IDraftSegmentsFormValues {
	id?: string;
	products?: ProductDto[];
	spaces?: SpaceResourceDto[];
	instructors?: InstructorResourceDto[];
	sport?: SportsEnum;
	customers?: SimpleCustomerDto[];
	series: IDraftSeriesFormValues[];
	visibility: VisibilityEnum;
	participantCount?: number;
	facility: SimpleFacilityDto;
	title: string;
	publicNotes?: string;
	privateNotes?: string;
	allProducts?: boolean;
	colorCodeId: number;
	segmentType?: SegmentTypeEnum;
	passMode?: PassModeEnum;
	userPunchPassId?: number;
	userPunchPass?: UserPunchPassDto;
	passProduct?: ProductDto;
}

export interface IDraftSeriesFormValues {
	/** Saved draft series id, set once the form is loaded from the draft. */
	id?: string;
	startDate: string;
	startTime: string;
	endDate: string;
	endTime: string;
	frequency: FrequencyEnum;
	repeatEvery?: number;
	repeatOn?: number[];
	repeatEndDate?: string;
	numberOfOccurrences?: number;
	maintenance?: MaintenanceDto[];
	isMaintenance?: boolean;
	isOccurrences?: boolean;
	isEndDate?: boolean;
	isRepeat?: boolean;
}

export type UpdateDraftReservation<T> = T extends Partial<IDraftReservationFromValues> ? T : never;
export interface IDraftReservationFromValues extends DraftReservationAdditionalSteps {
	reservationId?: string;
	name: string;
	reservationType: ReservationTypeEnum;
	[VISIBILITY]: VisibilityEnum;
	colorCodeId: number;
	segments: IDraftSegmentsFormValues[];
	[CUSTOMER]: ICustomer;
	customerId: number;
	privateNotes?: string;
	publicNotes?: string;
	addToReservationId?: number;
}

export enum VisibilityEnum {
	PUBLIC = 'public',
	PRIVATE = 'private',
	//Hide is not for next phases
	//HIDE = 'Hide',
}

export interface GererateReservationError {
	err?: ProblematicSlotDto[];
	status: HttpStatusEnum;
}
